/**
 * Permission helpers.
 *
 * CORE RULE: Permission decisions are a BACKEND responsibility. These
 * helpers are UX-only — they hide or show controls to reduce confusion.
 * The server still enforces every permission and returns 403 if a
 * forbidden action is attempted.
 */
import React from "react";
import { useAuth } from "@/context/AuthContext";

/**
 * usePermission("order.confirm") → boolean
 * Also accepts an array (any-of).
 */
export function usePermission(permissionOrAny) {
  const { permissions } = useAuth();
  const set = new Set(permissions || []);
  if (Array.isArray(permissionOrAny)) {
    return permissionOrAny.some((p) => set.has(p));
  }
  return set.has(permissionOrAny);
}

/**
 * Returns true if the current user has ALL of the listed permissions.
 */
export function useAllPermissions(list = []) {
  const { permissions } = useAuth();
  const set = new Set(permissions || []);
  return list.every((p) => set.has(p));
}

/**
 * <Can perm="order.confirm">...</Can>
 * <Can perm={["order.confirm","order.approve"]}>...</Can>  (any-of)
 * <Can perm="order.confirm" fallback={<Locked/>}>...</Can>
 */
export function Can({ perm, children, fallback = null }) {
  const allowed = usePermission(perm);
  return allowed ? <>{children}</> : fallback;
}
